import { electron } from 'lib-shared-electron';
import { Sentry } from 'lib-shared-sentry';
import { AnyAction, Middleware } from 'redux';

import { appActions } from 'app/app.actions';
import { busylightActions } from 'app/views/layout/views/settings/views/preferences/views/preferencesConnectedDevices/views/busylight/busylight.actions';
import { AppState } from 'bootstrap.store';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { presenceActions } from 'components/presence/actions/presenceActions';
import { jabraActions } from 'middleware/jabra';
import { VoiceConversationOffer } from 'middleware/jabra/jabra.types';
import { twilioActions } from 'middleware/twilio/actions/twilioActions';
import { websocketActions } from 'middleware/websocket/actions/websocket';

import { electronActions } from './electron.actions';
import {
  ElectronEventRequest,
  ElectronEventResponse,
  IpcRendererChannel,
} from './electron.types';

/**
 * The electron middleware to facilitate communication between the web
 * application and the desktop app.
 */
export const electronMiddleware: () => Middleware<unknown, AppState> =
  () => (store) => {
    const voiceConversationOffers: VoiceConversationOffer[] = [];
    /** If twilio has established a call connection */
    let hasEstablishedCall = false;

    /**
     * Sends an event through the ipc renderer.
     *
     * @param request The outgoing event.
     */
    const sendEvent = async (request: ElectronEventRequest) => {
      if (!window.electron) {
        return;
      }

      window.electron?.ipcRenderer.send(
        IpcRendererChannel.AsynchronousMessage,
        request,
      );
    };

    /**
     * Receives an event from the ipc renderer and dispatches the appropriate
     * redux actions.
     *
     * @param event The ipc renderer event.
     * @param response The incoming event.
     */
    const onEvent = (_, response: ElectronEventResponse) => {
      if (response.type) {
        switch (response.type) {
          case 'ELECTRON_MAKE_CALL':
            store.dispatch(electronActions.makeCall(response.payload));
            break;
          case 'ELECTRON_COMPOSE_EMAIL':
            store.dispatch(electronActions.composeEmail(response.payload));
            break;
          case 'BUSYLIGHT_GET_CONNECTED_STATUS_SUCCEEDED':
            store.dispatch(
              busylightActions.getConnectedStatusFulfilled(response.payload),
            );
            break;
          case 'BUSYLIGHT_RING_SUCCEEDED':
            store.dispatch(busylightActions.ringFulfilled());
            break;
          case 'BUSYLIGHT_RING_FAILED':
            store.dispatch(busylightActions.ringRejected());
            break;
          case 'BUSYLIGHT_STOP_SUCCEEDED':
            store.dispatch(busylightActions.stopFulfilled());
            break;
          case 'JABRA_CLOSED':
            store.dispatch(jabraActions.jabraClosed());
            break;
          case 'JABRA_INIT_SUCCEEDED':
            store.dispatch(jabraActions.jabraInitSucceeded());
            break;
          case 'JABRA_LIST_SUCCEEDED':
            store.dispatch(
              jabraActions.jabraListSucceeded({
                ...response.payload,
                list: (response.payload.list as any[]).map((device) => ({
                  ...device,
                  isLocked: true,
                })),
              }),
            );
            break;
          case 'JABRA_LIST_FAILED':
            store.dispatch(jabraActions.jabraListFailed(response.payload));
            break;
          case 'JABRA_RING_SUCCEEDED':
            store.dispatch(jabraActions.jabraRingSucceeded(response.payload));
            break;
          case 'JABRA_RING_FAILED':
            store.dispatch(jabraActions.jabraRingFailed(response.payload));
            break;
          case 'JABRA_UNRING_SUCCEEDED':
            store.dispatch(jabraActions.jabraUnringSucceeded(response.payload));
            break;
          case 'JABRA_UNRING_FAILED':
            store.dispatch(jabraActions.jabraUnringFailed(response.payload));
            break;
          case 'JABRA_DETACHED':
            store.dispatch(jabraActions.jabraDetached(response.payload));
            break;
          case 'JABRA_ATTACHED':
            store.dispatch(
              jabraActions.jabraAttached({
                ...response.payload,
                device: { ...(response.payload.device as any), isLocked: true },
              }),
            );

            // Stop the jabra device from ringing if we do not have any conversation offers.
            if (voiceConversationOffers.length === 0) {
              sendEvent({ type: 'JABRA_UNRING' });
            }
            break;
          case 'JABRA_BUTTON_CLICKED':
            if (voiceConversationOffers.length > 0) {
              const [voiceConversationOffer] = voiceConversationOffers;
              const csid =
                voiceConversationOffer.csid !== undefined
                  ? voiceConversationOffer.csid
                  : null;
              const isInternal = voiceConversationOffer.isInternal;
              const transferType = voiceConversationOffer.transferType
                ? voiceConversationOffer.transferType
                : null;
              const isEndUserConnected =
                voiceConversationOffer.isEndUserConnected === false
                  ? false
                  : true;
              const channel =
                voiceConversationOffer.channel !== undefined
                  ? voiceConversationOffer.channel
                  : 'pstnphone';
              if (csid) {
                if (
                  response.payload.button === 'OffHook' &&
                  response.payload.buttonValue === true
                ) {
                  store.dispatch(
                    jabraActions.jabraButtonClickedAcceptedCallOfferConversation(
                      {
                        did_accept: true,
                        csid,
                        channel,
                        isInternal,
                        isEndUserConnected,
                        transferType,
                      },
                    ),
                  );
                } else if (response.payload.button === 'RejectCall') {
                  store.dispatch(
                    jabraActions.jabraButtonClickedRejectedCallOfferConversation(
                      {
                        did_accept: false,
                        csid,
                        isInternal,
                        isEndUserConnected,
                        transferType,
                      },
                    ),
                  );
                } else {
                  store.dispatch(
                    jabraActions.jabraButtonClicked(response.payload),
                  );
                }
              }
            } else {
              store.dispatch(jabraActions.jabraButtonClicked(response.payload));
            }
            break;
          default:
            console.warn(
              'The received electron event type is unknown',
              response,
            );
        }
      }
    };

    /** Initializes electron. */
    const initialize = async () => {
      const electron = window.electron;

      // Setup event receiver and disposal logic.
      if (electron) {
        electron.ipcRenderer.on(IpcRendererChannel.AsynchronousReply, onEvent);

        window.onbeforeunload = () => {
          electron.ipcRenderer.removeListener(
            IpcRendererChannel.AsynchronousReply,
            onEvent,
          );

          sendEvent({ type: 'ELECTRON_DISPOSE' });
        };
      }

      sendEvent({ type: 'BUSYLIGHT_GET_CONNECTED_STATUS' });
      sendEvent({ type: 'JABRA_LIST' });

      store.dispatch(electronActions.connected({ connected: true }));
    };

    return (next) => (action: AnyAction) => {
      try {
        if (electron.isElectron()) {
          const state = store.getState();

          switch (action.type) {
            case websocketActions.createFulfilled.type:
              if (action.payload.init === true) {
                initialize();
              }
              break;
            case conversationActions.conversationWsTransferOfferReceived.toString():
            case conversationActions.conversationWsOfferReceived.toString(): {
              if (
                ['pstnphone', 'callback'].includes(
                  action.payload?.params?.channel,
                )
              ) {
                voiceConversationOffers.push({
                  csid: action.payload.params.csid,
                  reassignment: action.payload.params.reassignment,
                  isInternal: action.payload.params.direction === 'internal',
                  transferType: action.payload.params.transfer_type
                    ? action.payload.params.transfer_type
                    : null,
                });
              }
              const { busylight } = state.settings.personal;

              sendEvent({
                type: 'ELECTRON_CONVERSATION_OFFER_RECEIVED',
                payload: {
                  busylight,
                  channel: action.payload?.params?.channel,
                },
              });
              break;
            }
            case conversationActions.conversationRespondeToOfferAccepted.toString():
            case conversationActions.conversationRespondeToOfferRejected.toString():
            case conversationActions.conversationRespondeToTransferOfferAccepted.toString():
            case conversationActions.conversationRespondeToTransferOfferRejected.toString():
            case conversationActions.conversationWsOfferCanceled.toString(): {
              const index = voiceConversationOffers.findIndex(
                (offer) =>
                  offer.csid ===
                  (action.payload?.params?.csid || action.payload.csid),
              );
              if (index > -1) {
                voiceConversationOffers.splice(index, 1);
              }
              sendEvent({ type: 'ELECTRON_CONVERSATION_OFFER_CANCELLED' });
              break;
            }
            case conversationActions.conversationGetSucceeded.toString():
              if (!window.spellCheckHandler) {
                // For desktop app version > 4.0.3
                // Takes all content from the last 4 conversation and sends the text to the electron app
                // so it can try detect the language and add it to the spellchecker.
                const textForLanguageDetection =
                  action.payload.data.history.main_conversation.events
                    .filter(({ type }) => type === 'conversation:message_added')
                    .slice(-4)
                    .map((event) => {
                      const temporaryHtmlElement =
                        document.createElement('div');
                      // TODO: [TEAMS-12085] Investigate improper use of innerHTML
                      temporaryHtmlElement.innerHTML = event.text;

                      return (
                        temporaryHtmlElement.textContent ||
                        temporaryHtmlElement.innerText
                      ).replace(/\s\s+/g, ' ');
                    })
                    .join(' ');

                sendEvent({
                  type: 'SPELLCHECKER_AUTO_DETECT_LANGUAGE',
                  payload: {
                    text: textForLanguageDetection,
                  },
                });
              } else {
                // For legacy desktop app version < 4.0.0
                action.payload.data.history.main_conversation.events
                  .filter(
                    (event) =>
                      event?.text &&
                      typeof event.text === 'string' &&
                      event.text.trim() !== '' &&
                      event.text.length > 10,
                  )
                  .forEach((event) => {
                    // We try to guess the spellcheck language from the first text message in the event of the conversations.
                    // Remember this does not work on Mac!
                    window.spellCheckHandler.provideHintText(event.text);
                  });
              }
              break;
            case appActions.logOut.fulfilled.type:
              sendEvent({ type: 'ELECTRON_APP_SIGN_OUT' });
              break;
            case presenceActions.presenceSetStatusSucceeded.toString():
              sendEvent({
                type: 'ELECTRON_PRESENCE_CHANGE',
                payload: {
                  status:
                    action.payload.data?.presence_status_category ||
                    action.payload.params?.new_status ||
                    'offline',
                },
              });
              break;
            case presenceActions.presenceWsSetOwnStatus.toString():
              // Don't update presence status when a twilio call is established
              if (hasEstablishedCall) {
                break;
              }

              sendEvent({
                type: 'ELECTRON_PRESENCE_CHANGE',
                payload: {
                  status: action.payload.params?.new_status || 'offline',
                },
              });
              break;
            case presenceActions.presenceGetAllSucceeded.toString(): {
              const agentPresence = action.payload.data.find(
                (userPresence) =>
                  userPresence.user.id === action.payload.user_id,
              );
              if (agentPresence) {
                sendEvent({
                  type: 'ELECTRON_PRESENCE_CHANGE',
                  payload: {
                    status: agentPresence?.presence?.status || 'offline',
                  },
                });
              }
              break;
            }
            case twilioActions.twilioEstablishedCall.toString():
              // Let other parts react to a call having been established
              hasEstablishedCall = true;
              sendEvent({ type: 'TWILIO_ESTABLISHED_CALL' });
              break;
            case twilioActions.twilioDisconnectedCall.toString(): {
              // We need to validate the there is no active transfer waiting.
              // If there is we need postpone the request to the Desktop app.
              // Because transfer to agent/number (both blind and assisted) is disconnecting the call via Twilio SDK
              // And the disconnect from Twilio SDK is a bit faster than the WS event us telling the reducer
              // that the current agent not is assigned anymore.

              // What happens in steps is.:
              // 1. Twilio SDK tells that the current connection is disconnected
              // 2. Jabra integration listens to TWILIO_DISCONNECTED_CALL and then fires device.deviceOnhook().
              // 3. Jabra SDK fires a button click event telling us that a hangup button was pressed (https://github.com/gnaudio/jabra-node-sdk/issues/36).
              // 4. This Jabra saga then listens to device btn clicked "OffHook" with value "false"
              // 5. If it finds any telephony conversation(s) that is not closed, the current agent is assigned to and is of the type phone/callback closes the conversation.
              // By postponing the request to the desktop app. We give the backend and AI time to unassign the current agent.
              const transferToAgentMetaCsid = state.transferToAgent
                .metaConversation.conversation.id as number;
              const transferToNumberMetaCsid = state.transferToNumber
                .metaConversation.conversation.id as number;
              const { conference, internal, outbound } =
                action.payload.message ?? {};
              const csid = conference | internal | outbound;
              const activeTransferCsid =
                transferToAgentMetaCsid | transferToNumberMetaCsid;

              if (activeTransferCsid === csid) {
                setTimeout(() => {
                  sendEvent({ type: 'TWILIO_DISCONNECTED_CALL' });
                  sendEvent({
                    type: 'ELECTRON_PRESENCE_CHANGE',
                    payload: { status: state.presence.presence.status },
                  });
                }, 5000);
              } else {
                sendEvent({ type: 'TWILIO_DISCONNECTED_CALL' });
                sendEvent({
                  type: 'ELECTRON_PRESENCE_CHANGE',
                  payload: { status: state.presence.presence.status },
                });
              }

              hasEstablishedCall = false;
              break;
            }
            case busylightActions.stopFulfilled.toString():
              sendEvent({
                type: 'ELECTRON_PRESENCE_CHANGE',
                payload: { status: state.presence.presence.status },
              });
              break;
            case busylightActions.ring.toString():
              sendEvent({
                type: 'BUSYLIGHT_RING',
                payload: {
                  busylight:
                    action.payload?.busylight ||
                    state.settings.personal.busylight,
                },
              });
              break;
            case busylightActions.stop.toString():
              sendEvent({ type: 'BUSYLIGHT_STOP' });
              break;
            case busylightActions.getConnectedStatus.toString():
              sendEvent({ type: 'BUSYLIGHT_GET_CONNECTED_STATUS' });
              break;
            case jabraActions.jabraRing.toString():
              sendEvent({ type: 'JABRA_RING' });
              break;
            case jabraActions.jabraUnring.toString():
              sendEvent({ type: 'JABRA_UNRING' });
              break;
            case jabraActions.jabraList.toString():
              sendEvent({ type: 'JABRA_LIST' });
              break;
            case jabraActions.jabraInit.toString():
              sendEvent({ type: 'JABRA_INIT' });
              break;
            /**
             * Clear event cache is a "hack" to enable electron to avoid
             * disregarding the next event message. The desktop app is blocking
             * consecutive equal events to avoid issues we have with sending too
             * many events
             *
             * The desktop app have no knowledge of this event, but it will make
             * it listen for the next event even if it is the same.
             */
            case electronActions.clearEventCache.toString():
              sendEvent({ type: 'ELECTRON_CLEAR_EVENT_CACHE' });
          }
        }
      } catch (exception) {
        Sentry.captureException(
          new Error('Electron: middleware exception caught'),
          { extra: { exception } },
        );
      }

      return next(action);
    };
  };
